import React, { useState } from "react";

import classnames from "classnames";
import PrimepayLogo from "images/logos/png/simplyinsured__primepay--white.png";
import SquarePartnerLogo from "images/logos/si_square_darkbg.png";
import SimplyInsuredLogo from "images/simplyinsured-logo-inverted-small.png";
import isPrimepay from "Primepay/isPrimepay";
import { quotePageName } from "QuotePage/urls";
import { Link } from "react-router-dom";
import { reduxForm, SubmissionError } from "redux-form";
import isSquare from "Square/isSquare";
import { companyTokenString } from "Utils/companyToken";
import { routerUrlHelper } from "Utils/routerUrl";

import { useGetApplicationQuery, useUpdateAccountMutation } from "../api";
import { BenefitsBox } from "../components/BenefitsBox";
import { Email, Password } from "../components/CommonQuestions";
import { baseURL, companyInformationURL } from "../urls";
import { H1, Title, Subtitle } from "Cobalt/Header";
import Button from "Common/Button";
import { SIFormField, SIFormRadioGroup } from "Common/forms";
import { SaveButton } from "Common/forms/buttons";
import { Grid, Row, Col } from "Common/Grid";
import { appendAuthParams } from "Common/TokenAuth/Utils";

import makeBem from "Utils/makeBem";

const bem = makeBem("LandingPage");

const withReduxForm = reduxForm({
  form: "benefitsCurrentlyOffered",
});

const PartnerLogo = () => {
  let logo = <img className={bem("Logo")} src={SimplyInsuredLogo} />;

  if (isSquare()) {
    logo = <img className={bem("Logo")} src={SquarePartnerLogo} />;
  } else if (isPrimepay()) {
    logo = <img className={bem("Logo")} src={PrimepayLogo} />;
  }

  return logo;
};

const withReduxFormCreateAccount = reduxForm({
  form: "createAccount",
  onSubmit: async (values, _dispatch, props) => {
    const { password, email } = values;
    try {
      const { redirect_to } = await props.updateAccount({ password, email }).unwrap();
      if (redirect_to) {
        window.location.href = redirect_to;
      }
    } catch (e) {
      const {
        data: { redirect_to },
      } = e;
      throw new SubmissionError({ redirect_to, email });
    }
  },
  onSubmitFail: ({ redirect_to, email }, _dispatch, _submitError, _props) => {
    if (redirect_to) {
      window.location.href = `${redirect_to}?email=${encodeURIComponent(email)}`;
    }
  },
});

const CreateAccountForm = withReduxFormCreateAccount(({ handleSubmit, submitting }) => (
  <form onSubmit={handleSubmit}>
    <Title className={bem("formLabel")}>
      Create Account or{" "}
      <a href="/login" className={bem("signInLink")}>
        Sign in here
      </a>
    </Title>
    <Row>
      <Email sm={4} />
    </Row>
    <Row>
      <Password sm={4} />
    </Row>
    <Row>
      <Col>
        <SaveButton mb={4} submitting={submitting} buttonText="Create Account" float="left" />
      </Col>
    </Row>
  </form>
));

const LandingPage = () => {
  const { data: borApplication } = useGetApplicationQuery();
  const { name: companyName, signedOut } = borApplication;

  const [selectedOption, setSelectedOption] = useState(null);
  const [updateAccount] = useUpdateAccountMutation();

  const onValueChange = (value) => {
    setSelectedOption(value);
  };

  const formattedUrl = appendAuthParams(routerUrlHelper(`${companyTokenString()}${quotePageName(false)}`));

  const buttonProps = {
    fullWidth: true,
    size: "medium",
    disabled: !selectedOption,
    ...(selectedOption === "Yes" && {
      component: Link,
      to: `${baseURL}${companyInformationURL}`,
    }),

    ...(selectedOption === "No" && {
      href: formattedUrl,
    }),
  };

  return (
    <Grid>
      <Row className={bem("heroRow")}>
        <Col md={7} lg={8}>
          <Title className={bem("heroReferral")}>
            <PartnerLogo />
          </Title>
          <H1 className={bem("heroTitle")}>Connect existing&nbsp;plan</H1>
          <Subtitle pt={4} className={bem("heroTitle")}>
            When you complete this process, we will integrate your current health plan into your payroll system and make SimplyInsured your broker of record.
          </Subtitle>
        </Col>
        <Col md={5} lg={4} className={classnames(bem("benefitsBoxCol"), "max-md:hidden")}>
          <div className={bem("benefitsBoxColWrapper")}>
            <BenefitsBox />
          </div>
        </Col>
      </Row>
      <Row className={bem("mainContent")}>
        <Col md={7} lg={8}>
          <Row>
            {!signedOut && (
              <Col>
                <Title className={bem("formLabel")}>
                  {companyName && `Does ${companyName} currently offer medical insurance benefits?`}
                  {!companyName && "Does your company currently offer medical insurance benefits?"}
                </Title>
                <SIFormField
                  name="typeOfContribution"
                  component={SIFormRadioGroup}
                  props={{
                    options: {
                      Yes: "Yes",
                      No: "No",
                    },
                  }}
                  onChange={onValueChange}
                />
              </Col>
            )}
          </Row>
          {signedOut ? (
            <CreateAccountForm updateAccount={updateAccount} />
          ) : (
            <Row className="w-full m-0">
              <Col className="px-0" smOffset={8} mdOffset={9} lgOffset={10}>
                <Button {...buttonProps}>Next</Button>
              </Col>
            </Row>
          )}
        </Col>
      </Row>
    </Grid>
  );
};

export default withReduxForm(LandingPage);
